import { useId, useRef, useState } from "react"
import { PopoverClose } from "@radix-ui/react-popover"
import { CircleAlertIcon, DownloadIcon, Trash2Icon, UploadIcon } from "lucide-react"

import { Button, buttonVariants } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  ConfigImportError,
  EXPORT_FILE_NAME,
  downloadTextFile,
  parseConfigText,
  serializeConfig,
  type SummarizerConfig,
} from "@/utils/summarizer-config"

interface ImportExportBarProps {
  config: SummarizerConfig
  invalidCounts: Record<string, number>
  canImport: boolean
  canClear: boolean
  onImport: (config: SummarizerConfig) => void
  onClear: () => void
}

export function ImportExportBar({
  config,
  invalidCounts,
  canImport,
  canClear,
  onImport,
  onClear,
}: ImportExportBarProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const clearTitleId = useId()
  const clearDescriptionId = useId()
  const [importError, setImportError] = useState<string | null>(null)

  const invalidLabelNames = Object.keys(invalidCounts).sort((a, b) => a.localeCompare(b))
  const isExportBlocked = invalidLabelNames.length > 0

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.currentTarget
    const file = input.files?.[0]

    input.value = ""
    if (file == null) return

    try {
      onImport(parseConfigText(await file.text()))
      setImportError(null)
    } catch (error) {
      setImportError(error instanceof ConfigImportError ? error.message : "The file could not be read")
    }
  }

  function exportConfig() {
    if (isExportBlocked) return

    downloadTextFile(EXPORT_FILE_NAME, serializeConfig(config))
  }

  return (
    <div className="flex min-w-0 flex-col gap-2 max-lg:w-full">
      <div className="flex flex-wrap items-center gap-2">
        {canImport && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="application/json,.json"
              className="hidden"
              aria-label="Import configuration file"
              onChange={(e) => void handleFileChange(e)}
            />
            <Button
              type="button"
              variant="outline"
              className="max-lg:flex-1"
              onClick={() => fileInput.current?.click()}
            >
              <UploadIcon /> Import
            </Button>
          </>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={isExportBlocked}
          className="disabled:pointer-events-auto disabled:cursor-not-allowed max-lg:flex-1"
          onClick={exportConfig}
        >
          <DownloadIcon /> Export
        </Button>
        {canClear && (
          <Popover>
            <PopoverTrigger
              type="button"
              disabled={Object.keys(config).length === 0}
              className={buttonVariants({ variant: "outline", className: "max-lg:w-full" })}
            >
              <Trash2Icon aria-hidden="true" /> Clear configuration
            </PopoverTrigger>
            <PopoverContent
              align="end"
              aria-labelledby={clearTitleId}
              aria-describedby={clearDescriptionId}
              className="w-80 max-w-(--radix-popover-content-available-width) min-w-0 overflow-y-auto"
            >
              <h3 id={clearTitleId} className="font-semibold">
                Clear configuration?
              </h3>
              <div id={clearDescriptionId} className="mt-2 space-y-3 text-sm text-pretty">
                <p>
                  Clear all labels, search terms, and classification settings for the selected client and brand. This
                  action cannot be undone.
                </p>
                <p>Export the configuration first if you want to keep a copy.</p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3 pt-4">
                <PopoverClose type="button" className={buttonVariants({ variant: "outline" })}>
                  Cancel
                </PopoverClose>
                <PopoverClose type="button" className={buttonVariants({ variant: "destructive" })} onClick={onClear}>
                  Clear configuration
                </PopoverClose>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {isExportBlocked && (
        <p role="alert" className="flex items-start gap-1 text-sm text-destructive">
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 max-lg:[overflow-wrap:anywhere]">
            Export is blocked until invalid search terms are fixed. Check: {invalidLabelNames.join(", ")}
          </span>
        </p>
      )}

      {importError != null && (
        <p role="alert" className="flex items-start gap-1 text-sm text-destructive">
          <CircleAlertIcon className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 max-lg:[overflow-wrap:anywhere]">Import failed: {importError}</span>
        </p>
      )}
    </div>
  )
}
